import React from 'react'
import { Box, Stack, Typography, alpha } from '@mui/material'
import { X } from 'lucide-react'
import { border, font } from 'settings/tokens';
import { surfaceRadius, tileRadius } from 'settings/brandRadius';

export type CertificatesChipListProps = {
  certificates: {
    name?: string
    id: string
    /** VRG-286 — the CHIP UID. Authenticated payloads only (VRG-231). */
    tag_uid?: string
    product: { type: string; title?: string; images?: string[]; sku?: string }
    status: string
    type: string
  }[]
  ids: string[]
  showImages?: boolean
  label?: string
  limit?: number
  showAll: boolean
  onToggleShowAll: () => void
  onRemove: (id: string) => void
  chipTitleFontSize?: number
  chipPadding?: { px: number; py: number }
  /**
   * Render each selection as a full preview ROW (thumbnail + title + SKU) stacked
   * vertically inside a scroll cap, instead of a wrapping row of pill chips.
   *
   * Chips truncate to nothing useful once a title is long — "Louis Vuitton
   * Monogram Jacket" and "Louis Vuitton Monogram Scarf" chip identically — and a
   * wrapping row grows the sheet unpredictably as you add selections. Rows show
   * the same image you picked from and cap their own height.
   */
  stacked?: boolean
}

export const CertificatesChipList = ({
  certificates,
  ids,
  showImages = true,
  label,
  limit = 6,
  showAll,
  onToggleShowAll,
  onRemove,
  chipTitleFontSize = 13,
  chipPadding = { px: 1, py: 0.5 },
  stacked = false,
}: CertificatesChipListProps) => {
  const shownIds = showAll ? ids : ids.slice(0, limit)
  const showToggle = ids.length > limit

  if (stacked) {
    // Every selection is shown — no "show N more" toggle. The scroll cap does the
    // job the toggle was doing (bounding the sheet) without hiding anything, and
    // ~4.5 rows leaves the next row half-visible, which is what tells you the area
    // scrolls at all.
    return (
      <Stack spacing={1} sx={{ mt: 2 }}>
        {label && (
          <Typography sx={{ color: '#1f2937', fontSize: 14 }}>
            {label}: {ids.length}
          </Typography>
        )}
        <Stack
          spacing={0.5}
          sx={{
            maxHeight: 268,
            overflowY: 'auto',
            // Room for the scrollbar so it never sits on top of the remove button.
            pr: 0.5,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {ids.map(id => {
            const cert = certificates.find(c => c.id === id)
            const image = cert?.product?.images?.[0]
            return (
              <Stack
                key={id}
                direction='row'
                alignItems='center'
                spacing={1.5}
                sx={{
                  px: 1,
                  py: 1,
                  // VRG-562: a list ROW, so it follows the tenant's Corner radius.
                  // (The bare text chip further down does NOT — see the note there.)
                  borderRadius: surfaceRadius,
                  backgroundColor: theme => theme.palette.grey[50],
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    // VRG-562: clamped to the tile's own size — radius does not scale
                    // with the element, so an unclamped brand maximum would round a
                    // 40px thumbnail into a blob (`fieldMetrics.ts` makes the same
                    // argument for fields).
                    borderRadius: tileRadius(40),
                    overflow: 'hidden',
                    backgroundColor: theme => theme.palette.grey[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {image ? (
                    <img
                      src={image}
                      alt=''
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 40 40' fill='none'>
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M33 10H7V30H33V10ZM8.01961 29V11H31.9804V29H8.01961ZM14.6471 16C14.6471 17.1046 13.7341 18 12.6078 18C11.4816 18 10.5686 17.1046 10.5686 16C10.5686 14.8954 11.4816 14 12.6078 14C13.7341 14 14.6471 14.8954 14.6471 16ZM10.0588 27V24.5L14.6471 20L16.6863 22L23.8235 15L29.9412 21V27H10.0588Z'
                        fill='#8B8D8F'
                        opacity={0.5}
                      />
                    </svg>
                  )}
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cert?.product?.title ?? cert?.name ?? 'Unnamed certificate'}
                  </Typography>
                  {cert?.product?.sku && (
                    <Typography
                      sx={{
                        fontSize: font.label,
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cert.product.sku}
                    </Typography>
                  )}
                </Stack>
                {/* X, not a trash can: this drops the certificate from a SELECTION
                    and nothing is deleted (working-conventions §38). */}
                <Box
                  role='button'
                  tabIndex={0}
                  aria-label='Remove from selection'
                  onClick={() => onRemove(id)}
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    '&:hover, &:focus-visible': {
                      outline: 'none',
                      color: theme => theme.palette.error.main,
                      backgroundColor: theme => alpha(theme.palette.error.main, 0.08),
                    },
                  }}
                >
                  <X size={16} />
                </Box>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack spacing={1}>
      {label && (
        <Typography sx={{ color: '#1f2937' }}>
          {label}: {ids.length}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: showImages ? 1.5 : 1.5 }}>
        {shownIds.map(id => {
          const cert = certificates.find(c => c.id === id)
          return (
            <Box
              key={id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: showImages ? 1 : 1,
                backgroundColor: theme => theme.palette.grey[50],
                borderRadius: '12px',
                px: chipPadding.px,
                py: chipPadding.py,
              }}
            >
              {showImages && (
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '4px',
                    backgroundColor: 'gray.200',
                    border: '1px solid',
                    borderColor: border.subtle,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {cert?.product?.images?.[0] ? (
                    <img
                      src={cert.product.images[0]}
                      alt="certificate"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 40 40" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M33 10H7V30H33V10ZM8.01961 29V11H31.9804V29H8.01961ZM14.6471 16C14.6471 17.1046 13.7341 18 12.6078 18C11.4816 18 10.5686 17.1046 10.5686 16C10.5686 14.8954 11.4816 14 12.6078 14C13.7341 14 14.6471 14.8954 14.6471 16ZM10.0588 27V24.5L14.6471 20L16.6863 22L23.8235 15L29.9412 21V27H10.0588Z"
                        fill="#8B8D8F"
                        opacity={0.5}
                      />
                    </svg>
                  )}
                </Box>
              )}
              <Typography sx={{ fontSize: chipTitleFontSize }}>
                {cert?.product?.title ?? cert?.name ?? 'Unnamed certificate'}
              </Typography>
              <Box sx={{ cursor: 'pointer', fontSize: 16, fontWeight: 'bold', lineHeight: 1 }} onClick={() => onRemove(id)}>
                ×
              </Box>
            </Box>
          )
        })}
        {showToggle && (
          <Box
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              color: theme => theme.palette.primary.main,
              fontSize: showImages ? 12 : 13,
              alignSelf: 'center',
              px: 1,
            }}
            onClick={onToggleShowAll}
          >
            {showAll ? 'Show less' : 'Show all'}
          </Box>
        )}
      </Box>
    </Stack>
  )
}

export default CertificatesChipList


